// HandoffComposer — bottom sheet for writing a 12/24/72-hour note to
// tomorrow-you. Opens from a small link on BedtimePledgeCard.
//
// Hand-offs are operational: "if you wake up craving, remember water +
// headphones + 4-7-8". Pledges are aspirational ("what's the why").
// They live in different organs; the morning surface stacks pledge
// (italic line under headline) above hand-off card.
//
// SegmentedControl's generic is `<T extends string>`, so the TTL
// options are carried as the string keys '12' | '24' | '72' and
// parsed to numbers at the moment we POST. The server validation
// schema (HandoffCreateBody) only accepts those three literal values.

import { useState } from 'react';
import { BottomSheet } from '../../components/ui/BottomSheet';
import { Textarea } from '../../components/ui/Textarea';
import { SegmentedControl } from '../../components/ui/SegmentedControl';
import { Button } from '../../components/ui/Button';
import { apiPost } from '../../lib/api';
import { apiErrorMessage, isQueuedOffline } from '../../lib/apiErrors';
import { useToast } from '../../components/ui/Toast';

type ExpiresInHoursStr = '12' | '24' | '72';

interface HandoffComposerProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

export function HandoffComposer({ open, onClose, onSaved }: HandoffComposerProps) {
  const toast = useToast();
  const [text, setText] = useState('');
  const [expiresInHours, setExpiresInHours] = useState<ExpiresInHoursStr>('24');
  const [busy, setBusy] = useState(false);

  async function save() {
    const trimmed = text.trim();
    if (trimmed.length < 1) return;
    setBusy(true);
    try {
      await apiPost('/api/handoff', {
        body: trimmed,
        expiresInHours: parseInt(expiresInHours, 10),
      });
      toast.push('Saved. Tomorrow-you will see this first.');
      setText('');
      onSaved();
      onClose();
    } catch (err) {
      if (!isQueuedOffline(err)) {
        toast.push(apiErrorMessage(err, 'Could not save — try again.'), { tone: 'error' });
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <BottomSheet
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title="A note for tomorrow-you"
    >
      <p
        className="muted text-caption"
        style={{ marginBlockEnd: 'var(--space-3)' }}
      >
        Operational, not aspirational. "If you wake up craving: water + headphones." Keep it kind.
      </p>
      <Textarea
        label="Your note"
        value={text}
        maxLength={280}
        onChange={(e) => setText(e.target.value)}
        placeholder="If you wake up craving…"
        rows={4}
      />
      <div style={{ marginBlock: 'var(--space-3)' }}>
        <SegmentedControl<ExpiresInHoursStr>
          value={expiresInHours}
          onChange={setExpiresInHours}
          ariaLabel="How long it stays visible"
          options={[
            { value: '12', label: '12h' },
            { value: '24', label: '24h' },
            { value: '72', label: '72h' },
          ]}
        />
      </div>
      <Button
        onClick={() => void save()}
        loading={busy}
        disabled={text.trim().length < 1}
        size="md"
        block
      >
        Save for tomorrow
      </Button>
    </BottomSheet>
  );
}
